import AvatarCrop from 'core/modules/atoms/AvatarCrop';
import React from 'react';
import Dialog from '../Dialog/Dialog';
import useDialogCropImage from './useDialogCropImage';

/**
 * DialogCropImage — v2 organism.
 *
 * Modal que envuelve {@link AvatarCrop} para que el usuario suba una imagen
 * y la recorte/ajuste antes de confirmar. Compone {@link Dialog} en
 * `size="s"` con los botones Cancel / Save del footer.
 *
 * Accesibilidad (WCAG 2.1 AA):
 *  - El dialog hereda focus trap, cierre por `Escape`, `aria-modal="true"`
 *    y devolución de foco al trigger desde `Dialog` v2.
 *  - `title` es obligatorio y se usa como `aria-labelledby` del dialog para
 *    que los lectores de pantalla anuncien el propósito del modal.
 *  - El cropper (`react-avatar-edit`, vía `AvatarCrop` v1) es una superficie
 *    de selección con ratón. Esta limitación se hereda del atom v1 y queda
 *    documentada como deuda técnica hasta que se sustituya por un cropper
 *    accesible con teclado.
 *  - Indicador de foco visible en la zona de subida: el `<input type="file">`
 *    interno está visualmente oculto (estilo inline de `react-avatar-edit`),
 *    así que el outline se delega al contenedor padre vía `:has(...:focus-visible)`
 *    (ver `_dialog-crop-image.scss`).
 */

type DialogCropImageProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (image: string) => void;
  title: string;
}>;

function DialogCropImage({ isOpen, onClose, onSuccess, title }: DialogCropImageProps): React.JSX.Element {
  const { buttonTextSuccess, description, handleChange, handleDeleteImage, handleSuccess, metaInfo } = useDialogCropImage({
    onClose,
    onSuccess,
  });

  return (
    <Dialog
      buttonTextSuccess={buttonTextSuccess}
      className="dialog-crop-image"
      isOpen={isOpen}
      onCancel={onClose}
      onClose={onClose}
      onSuccess={handleSuccess}
      size="s"
      title={title}
    >
      <span className="dialog-crop-image__description">{description}</span>
      <div className="dialog-crop-image__cropper">
        <AvatarCrop onChange={handleChange} onClose={handleDeleteImage} />
      </div>
      <span className="dialog-crop-image__meta-info">{metaInfo}</span>
    </Dialog>
  );
}

export default DialogCropImage;
